/**
 * Pagina di callback OIDC (TD-004 — login SSO di sviluppo).
 *
 * Route pubblica `/auth/callback`: completa lo scambio code→token avviato da
 * startLogin() (LoginPage). In caso di successo aggiorna AuthContext e naviga
 * alla dashboard; in caso di errore (state non valido, scambio fallito) mostra
 * un messaggio e un link per tornare al login.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, OIDC_ISSUER, handleCallback, getToken } from '../auth/index.js';
import styles from './LoginPage.module.css';

export function AuthCallbackPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Guardia anti-doppia-esecuzione (React.StrictMode invoca gli effetti due volte
  // in sviluppo): lo scambio code→token è one-shot (sessionStorage viene ripulito
  // dal primo run), quindi il secondo run NON deve ripeterlo né mostrare errori.
  const startedRef = useRef(false);

  useEffect(() => {
    // Già autenticato (es. doppio mount in StrictMode): non ripetere lo scambio.
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    if (!OIDC_ISSUER) {
      setError('SSO non configurato (VITE_OIDC_ISSUER assente).');
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      try {
        await handleCallback(OIDC_ISSUER, window.location.search);
        // handleCallback ha già salvato l'id_token via setToken(); login() lo
        // ri-applica (idempotente) e aggiorna lo stato React (token + claims).
        const token = getToken();
        if (!token) throw new Error('Token non salvato dopo lo scambio OIDC.');
        login(token);
        navigate('/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Scambio token OIDC fallito.');
      }
    })();
  }, []);

  if (error) {
    return (
      <div className={styles.page} data-testid="auth-callback-page">
        <div className={styles.card}>
          <h1 className={styles.brand}>
            Tenacious<span className={styles.brandAccent}>C</span>
          </h1>
          <p className={styles.error} data-testid="sso-error">
            {error}
          </p>
          <a href="/login">Torna al login</a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="auth-callback-page">
      <div className={styles.card}>
        <p style={{ textAlign: 'center', color: '#6c757d' }}>Accesso in corso…</p>
      </div>
    </div>
  );
}
